import {
  Component,
  DestroyRef,
  EventEmitter,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  input
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MercerDataService, MercerValidationService } from '@coin/shared/master-data/data-access';
import { MercerJobCodeSearchablePropertyEnum } from '@coin/shared/master-data/util';
import { ConfirmationDialogComponent } from '@coin/shared/core/ui-storybook';
import { TinyHelpers } from '@coin/shared/core/util-helpers';
import { FieldValuesDescription, MercerData } from '@coin/shared/core/util-models';
import { firstValueFrom, Observable, of, Subject, Subscription } from 'rxjs';
import { debounceTime, filter, map, switchMap, tap } from 'rxjs/operators';
import { MercerJobType } from '../../../mercer/enums';
import { MercerJobProfile } from '../../../mercer/models/mercer-job-profile.model';
import { MercerDialogType } from '../../enums/mercer-dialog-type.enum';
import { MercerEvaluationMemberDetails } from '../../models/mercer-support-evaluation-member.model';
import { MercerSupportDetailsService } from '../../services/mercer-support-details.service';
import { NgModel } from '@angular/forms';

@Component({
  selector: 'customer-position-job-code-card',
  templateUrl: './position-job-code-card.component.html',
  styleUrls: ['./position-job-code-card.component.scss'],
  standalone: false
})
export class PositionJobCodeCardComponent implements OnInit, OnChanges {
  private mercerDataService = inject(MercerDataService);
  private mercerValidationService = inject(MercerValidationService);
  private dialog = inject(MatDialog);
  mercerSupportDetailsService = inject(MercerSupportDetailsService);
  private destroyRef = inject(DestroyRef);

  readonly profile = input<MercerJobProfile>(undefined);
  readonly mercerData = input<MercerData>(undefined);
  readonly isEditable = input(true);
  readonly dialogType = input<MercerDialogType>(undefined);
  readonly hideNextButton = input<boolean>(undefined);
  readonly mercerDataChanged$ = input<Subject<void>>(undefined);
  readonly fetchingData$ = input<Subject<boolean>>(undefined);
  readonly isQuestionnaireDisabledForGermanWithPartialEvaluation = input<boolean>(undefined);
  readonly memberDetails = input<MercerEvaluationMemberDetails>(undefined);
  readonly isLocked = input<boolean>(undefined);

  @Output() public setupNewMercerData = new EventEmitter<void>();
  @Output() public next = new EventEmitter<void>();
  @Output() public saveJobCode = new EventEmitter<void>();
  @Output() public openedEditMode = new EventEmitter<void>();

  public isInEditMode$: Observable<boolean>;
  public profileSelections: FieldValuesDescription[] = [];
  public search$ = new Subject<string>();
  public descriptionParts: string[];
  public profileTitle: string;
  public selectedMercerJobType$ = new Subject();
  public mercerDataCache: MercerData;
  public fetchingSubscription: Subscription;

  private page = 0;
  private lastSearch: string;

  public get isCareerLevel(): boolean {
    return this.profile()?.type === MercerJobType.CareerLevel;
  }

  public get isJobCodeSaveButtonDisabled(): boolean {
    return !this.profileTitle || !this.mercerDataCache?.jobCode;
  }

  public get isEditButtonVisible(): boolean {
    return (
      !this.isLocked() &&
      (this.dialogType() === MercerDialogType.Support ||
        ![MercerJobType.CareerStream, MercerJobType.CareerLevel].includes(this.profile()?.type))
    );
  }

  ngOnInit(): void {
    this.setupListeners();
    this.listenToSearch();
    this.listenToEditMode();
    this.listenToMercerDataChanged();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.mercerData) {
      this.mercerDataCache = TinyHelpers.nonThrowableStructuredCloneWorkaround(changes.mercerData.currentValue); // clone-problem: MercerData is a class, should not cause a problem yet though
    }
    if (changes.mercerData || changes.profile) {
      const mercerData = changes.mercerData ? changes.mercerData.currentValue : this.mercerData();
      const profile = changes.profile ? changes.profile.currentValue : this.profile();
      this.descriptionParts = TinyHelpers.splitByBulletPoints(
        mercerData?.[profile?.correlatingMercerDataDescriptionKey] || '-'
      );
      this.profileTitle = mercerData?.[profile.correlatingMercerDataTitleKey] || '';
    }
    // collapse card whenever position is saved
    if (changes.memberDetails) {
      this.cancel();
    }
  }

  private setupListeners(): void {
    this.isInEditMode$ = this.selectedMercerJobType$?.pipe(map(type => this.profile()?.type === type));
  }

  private listenToMercerDataChanged(): void {
    this.mercerDataChanged$()
      .pipe(
        tap(() => {
          this.mercerDataCache = TinyHelpers.nonThrowableStructuredCloneWorkaround(this.mercerData());
        })
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(); // clone-problem: MercerData is a class, should not cause a problem yet though
  }

  private reset(): void {
    this.fetchingSubscription?.unsubscribe();
    this.fetchingSubscription = undefined;
    this.fetchingData$().next(false);
  }

  public edit(): void {
    this.selectedMercerJobType$.next(this.profile().type);
    this.openedEditMode.emit();
  }

  public cancel(): void {
    this.reset();
    this.selectedMercerJobType$.next(null);
  }

  public goToNextProfile(): void {
    this.cancel();
    this.next.emit();
  }

  public save(): void {
    this.reset();
    this.saveJobCode.emit();
  }

  private applyChanges(): void {
    Object.assign(this.mercerData(), this.mercerDataCache);
    this.mercerDataChanged$().next();
    this.setupNewMercerData.emit();
  }

  public async onSelectionToggle(toggledAnswer: FieldValuesDescription, cardModel: NgModel): Promise<void> {
    if (toggledAnswer.title === this.profileTitle) {
      return;
    }

    if (!!this.profileTitle && toggledAnswer?.title !== this.profileTitle) {
      cardModel.control.setValue(false, { emitViewToModelChange: false }); // undo checkbox tick until it is confirmed via dialog
      const confirm = await this.overwritePopup(toggledAnswer?.title);
      if (!confirm) return;
      cardModel.control.setValue(true, { emitViewToModelChange: false });
    }

    this.fetchingData$().next(true);
    this.fetchingSubscription?.unsubscribe();
    const previousProfileTitle = this.profileTitle;
    const mercerData = TinyHelpers.nonThrowableStructuredCloneWorkaround(this.mercerDataCache); // clone-problem: MercerData is a class, should not cause a problem yet though
    this.profileTitle = toggledAnswer.title;
    this.fetchingSubscription = this.changeMercerJobCodeField(
      toggledAnswer,
      this.profile().correlatingMercerDataTitleKey,
      mercerData
    )
      .pipe(
        tap(() => {
          if (this.fetchingSubscription) {
            this.mercerDataCache = mercerData;
            this.applyChanges();
            if (this.profile()?.type === MercerJobType.CareerLevel) {
              this.resetItems();
              this.loadSelections();
            }
            this.fetchingSubscription?.unsubscribe();
            this.fetchingSubscription = undefined;
          } else {
            this.profileTitle = previousProfileTitle;
          }
          this.fetchingData$().next(false);
        })
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  public changeMercerJobCodeField(
    toggledAnswer: FieldValuesDescription,
    mercerKey: string,
    mercerData: MercerData
  ): Observable<unknown> {
    let observable$: Observable<unknown>;
    if (
      [MercerJobType.SubFamily, MercerJobType.Specialization, MercerJobType.CareerLevel].includes(this.profile().type)
    ) {
      observable$ = this.validateUpwards(toggledAnswer.title, mercerKey, mercerData);
    } else {
      observable$ = of(null).pipe(
        tap(() => {
          mercerData.jobCode = '';
          mercerData.jobTitle = '';
          mercerData[mercerKey] = toggledAnswer.title;
        })
      );
    }
    return observable$.pipe(switchMap(() => this.processMercerSubFieldChange(mercerKey, mercerData)));
  }

  private validateUpwards(value: string, mercerKey: string, mercerData: MercerData): Observable<unknown> {
    const usePartialJobCode = this.memberDetails()?.isGermanPartialEvaluation;
    return this.mercerValidationService
      .validateMercerUpwardsWithCurrentMercerData(mercerKey, value, mercerData, usePartialJobCode)
      .pipe(
        switchMap(() => this.mercerDataService.getMercerFieldValues(0, 1000, mercerKey, mercerData, '', true)),
        map(careerCheck => {
          if (!careerCheck?.content.length) {
            mercerData.careerLevel = '';
            mercerData.careerStream = '';
            mercerData.careerLevelShort = '';
          }
        })
      );
  }

  private overwritePopup(answerTitle: string): Promise<boolean> {
    return firstValueFrom(
      this.dialog
        .open(ConfirmationDialogComponent, {
          disableClose: true,
          data: {
            headline: 'job-validation.dialogs.overwrite-title',
            headlineData: answerTitle,
            translate: true,
            msg: 'mercer-support.dialog.overwrite-job-code-msg',
            confirmMsg: 'job-validation.buttons.proceed',
            cancelMsg: 'job-validation.buttons.cancel'
          }
        })
        .afterClosed()
    );
  }

  private processMercerSubFieldChange(mercerKey: string, mercerData: MercerData): Observable<unknown> {
    const usePartialJobCode = this.isQuestionnaireDisabledForGermanWithPartialEvaluation();
    return this.mercerDataService
      .getCorrelatingMercerData(mercerData, mercerKey as MercerJobCodeSearchablePropertyEnum, true, usePartialJobCode)
      .pipe(
        tap(data => this.mercerValidationService.setOtherMercerDataFieldsFor(data, mercerKey, mercerData)),
        switchMap(data =>
          this.mercerValidationService
            .getAllFieldValuesAndEnsureMercerCorrectness(mercerKey, mercerData)
            .pipe(tap(() => this.setValidJobCode(data, mercerData)))
        )
      );
  }

  private setValidJobCode(data: MercerData, mercerData: MercerData): void {
    if (
      data.family &&
      data.subFamily &&
      data.specialization &&
      ((mercerData.careerStream && mercerData.careerLevel) ||
        this.isQuestionnaireDisabledForGermanWithPartialEvaluation())
    ) {
      mercerData.jobCode = data?.jobCode;
      mercerData.jobTitle = data?.jobTitle;
    } else {
      mercerData.jobCode = '';
      mercerData.jobTitle = '';
    }
  }

  public onScroll(): void {
    this.page++;
    this.loadSelections(this.lastSearch);
  }

  private listenToSearch(): void {
    this.search$
      .pipe(
        debounceTime(350),
        filter(search => !search?.length || !!search?.trim()),
        tap(search => {
          this.resetItems();
          this.lastSearch = search;
        })
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(search => this.loadSelections(search));
  }

  private listenToEditMode(): void {
    this.isInEditMode$
      .pipe(
        filter(isInEditMode => !!isInEditMode),
        tap(() => this.resetItems())
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadSelections());
  }

  private resetItems(): void {
    this.profileSelections = [];
    this.page = 0;
  }

  private loadSelections(search?: string): void {
    const sortQuery = this.isCareerLevel ? '&Sorting.Property=CareerLevelShort&Sorting.OrderBy=asc' : '';

    this.mercerDataService
      .getMercerFieldValuesWithDescription(
        this.page,
        10,
        this.profile().correlatingMercerDataTitleKey,
        this.mercerData(),
        search,
        sortQuery
      )
      .pipe(
        tap(fieldValues => {
          this.profileSelections = [
            ...new Map(
              [...this.profileSelections, ...(fieldValues?.content ?? [])].map(selection => [
                selection.title,
                selection
              ])
            ).values()
          ]; // remove duplicates
        })
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }
}
